import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertCircle, CheckCircle } from 'lucide-react';
import emailApi from '../../services/emailApi';
import { markSubscribed } from '../../utils/subscription';

interface ThreatCategory {
  code: string;
  name: string;
  description?: string;
  color: string;
}

type ActiveTab = 'alerts' | 'digest';

interface SubscriptionPreferences {
  email: string;
  alertThreshold: number;
  alertCategories: string[];
  digestCategories: string[];
  language: 'en' | 'et' | 'ru' | 'ua';
}

export default function EmailSubscriptionForm() {
  const { t, i18n } = useTranslation();
  const digestEnabled = false;
  const [activeTab, setActiveTab] = useState<ActiveTab>('alerts');
  const [preferences, setPreferences] = useState<SubscriptionPreferences>({
    email: '',
    alertThreshold: 8.0,
    alertCategories: ['MILITARY'],
    digestCategories: ['MILITARY', 'CYBER', 'DIPLOMATIC'],
    language: (
      i18n.language?.startsWith('et') ? 'et' :
      i18n.language?.startsWith('ru') ? 'ru' :
      (i18n.language?.startsWith('ua') || i18n.language?.startsWith('uk')) ? 'ua' : 'en'
    ),
  });

  const [availableCategories, setAvailableCategories] = useState<ThreatCategory[]>([]);
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [message, setMessage] = useState('');

  // Load dynamic categories from API
  useEffect(() => {
    emailApi
      .getCategories()
      .then((categories) => setAvailableCategories(categories))
      .catch(() => setAvailableCategories([]));
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatus('loading');
    setMessage('');

    try {
      const payload = {
        email: preferences.email,
        enableAlerts: activeTab === 'alerts',
        alertThreshold: preferences.alertThreshold,
        alertCategories: preferences.alertCategories,
        enableDigest: digestEnabled && activeTab === 'digest',
        digestCategories: preferences.digestCategories,
        language: preferences.language,
      };

      await emailApi.subscribe(payload);
      setStatus('success');
      setMessage(t('subscription.success'));
      // Remember locally to avoid showing CTA/banner again
      try { markSubscribed(preferences.email); } catch {}
    } catch (err) {
      setStatus('error');
      setMessage(t('subscription.error'));
    }
  };

  const isAlertsTab = activeTab === 'alerts';
  const isValid =
    preferences.email.trim().length > 0 &&
    (isAlertsTab ? preferences.alertCategories.length > 0 : preferences.digestCategories.length > 0);

  return (
    <div className="max-w-md mx-auto p-6 bg-white dark:bg-gray-800 rounded-lg shadow-lg">
      <h2 className="text-xl font-bold mb-4 text-gray-900 dark:text-white">
        {t('subscription.title')}
      </h2>

      {/* Tabs */}
      <div className="flex mb-4 border-b border-gray-200 dark:border-gray-700">
        <button
          className={`px-4 py-2 text-sm font-medium -mb-px border-b-2 transition-colors ${
            isAlertsTab ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-600 hover:text-gray-800'
          }`}
          onClick={() => setActiveTab('alerts')}
          type="button"
          disabled={status === 'loading'}
        >
          {t('subscription.realtimeAlerts')}
        </button>
        {digestEnabled && (
        <button
          className={`ml-4 px-4 py-2 text-sm font-medium -mb-px border-b-2 transition-colors ${
            !isAlertsTab ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-600 hover:text-gray-800'
          }`}
          onClick={() => setActiveTab('digest')}
          type="button"
          disabled={status === 'loading'}
        >
          {t('subscription.weeklyDigest')}
        </button>
        )}
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Email Input */}
        <div>
          <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">
            {t('subscription.email')}
          </label>
          <input
            type="email"
            value={preferences.email}
            onChange={(e) => setPreferences({ ...preferences, email: e.target.value })}
            placeholder={t('subscription.emailPlaceholder')}
            className="w-full p-2 border rounded-md dark:bg-gray-700 dark:border-gray-600 dark:text-white"
            required
            disabled={status === 'loading'}
          />
        </div>

        {/* Alerts Tab Content */}
        {isAlertsTab && (
          <div className="space-y-4 border rounded-lg p-4 dark:border-gray-600">
            <h3 className="font-medium text-gray-900 dark:text-white">{t('subscription.alertSettings')}</h3>

            {/* Risk Threshold */}
            <div>
              <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">
                {t('subscription.alertThreshold', { value: preferences.alertThreshold })}
              </label>
              <input
                type="range"
                min="7"
                max="10"
                step="0.5"
                value={preferences.alertThreshold}
                onChange={(e) => setPreferences({ ...preferences, alertThreshold: parseFloat(e.target.value) })}
                className="w-full"
                disabled={status === 'loading'}
              />
              <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400 mt-1">
                <span>7.0</span>
                <span>8.5</span>
                <span>10.0</span>
              </div>
              <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">{t('subscription.thresholdHelp')}</p>
            </div>

            {/* Alert Categories */}
            <div>
              <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">
                {t('subscription.alertCategories')}
              </label>
              <div className="space-y-2">
                {availableCategories.map((category) => (
                  <label key={category.code} className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={preferences.alertCategories.includes(category.code)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setPreferences({
                            ...preferences,
                            alertCategories: [...preferences.alertCategories, category.code],
                          });
                        } else {
                          setPreferences({
                            ...preferences,
                            alertCategories: preferences.alertCategories.filter((c) => c !== category.code),
                          });
                        }
                      }}
                      className="rounded border-gray-300 dark:border-gray-600"
                      disabled={status === 'loading'}
                    />
                    <span className="text-sm text-gray-700 dark:text-gray-300">{category.name}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Digest Tab Content */}
        {digestEnabled && !isAlertsTab && (
          <div className="space-y-4 border rounded-lg p-4 dark:border-gray-600">
            <h3 className="font-medium text-gray-900 dark:text-white">{t('subscription.digestSettings')}</h3>

            {/* Digest Categories */}
            <div>
              <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">
                {t('subscription.digestCategories')}
              </label>
              <p className="text-xs text-gray-600 dark:text-gray-400 mb-2">{t('subscription.digestCategoriesHelp')}</p>
              <div className="space-y-2">
                {availableCategories.map((category) => (
                  <label key={category.code} className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={preferences.digestCategories.includes(category.code)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setPreferences({
                            ...preferences,
                            digestCategories: [...preferences.digestCategories, category.code],
                          });
                        } else {
                          setPreferences({
                            ...preferences,
                            digestCategories: preferences.digestCategories.filter((c) => c !== category.code),
                          });
                        }
                      }}
                      className="rounded border-gray-300 dark:border-gray-600"
                      disabled={status === 'loading'}
                    />
                    <span className="text-sm text-gray-700 dark:text-gray-300">{category.name}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Language */}
        <div>
          <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">
            {t('subscription.language')}
          </label>
          <select
            value={preferences.language}
            onChange={(e) => setPreferences({ ...preferences, language: e.target.value as any })}
            className="w-full p-2 border rounded-md dark:bg-gray-700 dark:border-gray-600 dark:text-white"
            disabled={status === 'loading'}
          >
            <option value="en">English</option>
            <option value="et">Eesti</option>
            <option value="ru">Русский</option>
            <option value="ua">Українська</option>
          </select>
        </div>

        {/* Submit Button */}
        <button
          type="submit"
          disabled={status === 'loading' || !isValid}
          className="w-full bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {status === 'loading' ? t('subscription.subscribing') : t('subscription.subscribe')}
        </button>

        {/* Status Messages */}
        {status === 'success' && (
          <div
            className="p-3 bg-green-100 text-green-900 border border-green-300 dark:bg-green-900 dark:text-green-100 dark:border-green-700 rounded-md text-sm flex items-center gap-2 shadow-sm"
            role="status"
            aria-live="polite"
          >
            <CheckCircle size={16} className="text-green-700 dark:text-green-300" />
            {message}
          </div>
        )}
        {status === 'error' && (
          <div
            className="p-3 bg-red-100 text-red-900 border border-red-300 dark:bg-red-900 dark:text-red-100 dark:border-red-700 rounded-md text-sm flex items-center gap-2 shadow-sm"
            role="alert"
            aria-live="assertive"
          >
            <AlertCircle size={16} className="text-red-700 dark:text-red-300" />
            {message}
          </div>
        )}
      </form>

      <p className="text-xs text-gray-500 dark:text-gray-400 mt-4">{t('subscription.disclaimer')}</p>
    </div>
  );
}
